import React from "react";
import { useI18n } from "@/lib/i18n";

const T = {
  it: {
    privacy: { h: "Privacy Policy", back: "Torna alla home" },
    cookie: { h: "Cookie Policy" },
    terms: { h: "Termini e condizioni" },
    a11y: { h: "Accessibilità" },
  },
  en: {
    privacy: { h: "Privacy Policy" },
    cookie: { h: "Cookie Policy" },
    terms: { h: "Terms & Conditions" },
    a11y: { h: "Accessibility" },
  },
};

function LegalShell({ title, children }) {
  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-16 lg:py-20">
      <h1 className="font-display text-4xl md:text-5xl text-brand mb-8">{title}</h1>
      <div className="prose-legal text-sm text-muted-ink leading-relaxed space-y-4">{children}</div>
      <style>{`
        .prose-legal h2 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 1.5rem; color: var(--brand); margin: 2em 0 0.5em; }
        .prose-legal p, .prose-legal li { line-height: 1.7; }
        .prose-legal ul { padding-left: 1.25em; }
        .prose-legal ul li { list-style: disc; margin: 0.4em 0; }
      `}</style>
    </div>
  );
}

export function PrivacyPage() {
  const { lang } = useI18n();
  if (lang === "en") {
    return (
      <LegalShell title="Privacy Policy">
        <p>Data controller: <strong>BioNutrix Srl</strong>, headquartered at Via C. Hamel 1, 92014 Porto Empedocle (AG), Italy. VAT IT03110550849. Email: bionutrix.supplements@gmail.com.</p>
        <h2>Data we process</h2>
        <p>We process the data you provide via contact forms, newsletter subscription, B2B commercial requests and Human line notify. We also process technical browsing data (with your consent, anonymised analytics).</p>
        <h2>Purposes</h2>
        <ul>
          <li>To respond to your information requests.</li>
          <li>To send subscription-related emails (double opt-in) and, subject to consent, marketing communications.</li>
          <li>To manage requests from professionals and resellers.</li>
          <li>Site improvement (only with consent to analytics cookies).</li>
        </ul>
        <h2>Legal basis</h2>
        <p>Consent of the data subject (Art. 6(a) GDPR) and legitimate interest for the technical operation of the site.</p>

        <h2>B2B Reserved Area — additional processing</h2>
        <p>The Reserved Area (<em>Area B2B</em>) is dedicated to authorised professional partners.</p>
        <p><strong>Data processed:</strong> first name, last name, business name, email, phone, VAT number, professional category, city, province, hashed password, IP hash on access, login/download timestamps, notes made by the administrator.</p>
        <p><strong>Purposes and legal basis:</strong></p>
        <ul>
          <li>Account provisioning and management (Art. 6(1)(b) GDPR — contractual necessity / pre-contractual measures).</li>
          <li>Security of the Reserved Area, prevention of abuse and audit (Art. 6(1)(f) GDPR — legitimate interest).</li>
          <li>Legal, tax and regulatory compliance (Art. 6(1)(c) GDPR).</li>
        </ul>
        <p><strong>Retention:</strong> account data are kept as long as the account is active and for a further 24 months after deactivation for security and dispute resolution purposes; access/download logs are kept for a maximum of 12 months; IP addresses are stored as a rotating hash (pseudonymised).</p>
        <p><strong>Recipients / technical providers:</strong> hosting (Emergent), email delivery (Resend, EU region — Ireland), private object storage (Emergent, EU region). No data are transferred outside the EEA without adequate safeguards.</p>
        <p><strong>Documents in the B2B area:</strong> reserved documents (certifications, technical sheets, catalogues, price lists, laboratory analyses, etc.) do not contain personal data of the user and are protected by short-lived signed URLs; direct or public access is prevented.</p>
        <p><strong>Rights of the data subject:</strong> you may at any time exercise the rights under Articles 15-22 GDPR (access, rectification, erasure, restriction, portability, objection). Requests: bionutrix.supplements@gmail.com.</p>
        <p><strong>Cookies:</strong> the Reserved Area uses only technical cookies necessary for authentication. No advertising cookies are used.</p>

        <h2>Rights of the data subject</h2>
        <p>You may at any time request access, rectification, erasure, restriction or portability of your data, and withdraw consent. Write to bionutrix.supplements@gmail.com.</p>
        <h2>Retention</h2>
        <p>We retain data for as long as necessary for the stated purposes and legal obligations.</p>
        <p className="italic text-xs">This policy will be updated by the administrator upon completion of legal review.</p>
      </LegalShell>
    );
  }
  return (
    <LegalShell title="Privacy Policy">
      <p>Titolare del trattamento: <strong>BioNutrix Srl</strong>, con sede in Via C. Hamel 1, 92014 Porto Empedocle (AG), Italia. P.IVA IT03110550849. Email: bionutrix.supplements@gmail.com.</p>
      <h2>Dati trattati</h2>
      <p>Trattiamo i dati che ci fornisci tramite i moduli di contatto, iscrizione newsletter, richiesta commerciale B2B, iscrizione al lancio della linea Human. Trattiamo inoltre dati tecnici di navigazione (con il tuo consenso, dati analitici anonimizzati).</p>
      <h2>Finalità</h2>
      <ul>
        <li>Rispondere alle tue richieste di informazioni.</li>
        <li>Inviare comunicazioni relative all'iscrizione (double opt-in) e, previo consenso, comunicazioni commerciali.</li>
        <li>Gestire richieste da parte di professionisti e rivenditori.</li>
        <li>Miglioramento del sito (solo con consenso ai cookie analitici).</li>
      </ul>
      <h2>Base giuridica</h2>
      <p>Consenso dell'interessato (art. 6 lett. a GDPR) e legittimo interesse per il funzionamento tecnico del sito.</p>

      <h2>Area Riservata B2B — trattamenti aggiuntivi</h2>
      <p>L'Area Riservata (<em>Area B2B</em>) è dedicata ai partner professionali autorizzati.</p>
      <p><strong>Dati trattati:</strong> nome, cognome, ragione sociale, e-mail, telefono, partita IVA, categoria professionale, città, provincia, password (memorizzata come hash sicuro), hash dell'indirizzo IP di accesso, orari di accesso e di download, note interne inserite dall'amministratore.</p>
      <p><strong>Finalità e basi giuridiche:</strong></p>
      <ul>
        <li>Creazione e gestione dell'account professionale (art. 6, par. 1, lett. b GDPR — esecuzione di misure precontrattuali/contrattuali).</li>
        <li>Sicurezza dell'Area Riservata, prevenzione di abusi e attività di audit (art. 6, par. 1, lett. f GDPR — legittimo interesse).</li>
        <li>Adempimento di obblighi legali, fiscali e regolatori applicabili (art. 6, par. 1, lett. c GDPR).</li>
      </ul>
      <p><strong>Conservazione:</strong> i dati dell'account sono conservati per tutta la durata dell'account e per ulteriori 24 mesi dalla disattivazione ai fini di sicurezza e gestione di eventuali contenziosi; i log di accesso e download sono conservati per un massimo di 12 mesi; gli indirizzi IP vengono memorizzati come hash pseudonimizzato non riconducibile.</p>
      <p><strong>Destinatari / fornitori tecnici:</strong> servizi di hosting (Emergent), invio email (Resend, region EU — Ireland), object storage privato (Emergent, region EU). Non vengono effettuati trasferimenti di dati al di fuori dello Spazio Economico Europeo senza adeguate garanzie.</p>
      <p><strong>Documenti dell'Area B2B:</strong> i documenti riservati (certificazioni, schede tecniche, cataloghi, listini, analisi di laboratorio, ecc.) non contengono dati personali dell'utente e sono protetti da URL firmati temporanei; l'accesso diretto o pubblico è impedito.</p>
      <p><strong>Diritti dell'interessato:</strong> puoi in qualsiasi momento esercitare i diritti di cui agli artt. 15-22 GDPR (accesso, rettifica, cancellazione, limitazione, portabilità, opposizione) scrivendo a bionutrix.supplements@gmail.com.</p>
      <p><strong>Cookie:</strong> l'Area B2B utilizza esclusivamente cookie tecnici necessari all'autenticazione. Non sono utilizzati cookie pubblicitari o di profilazione.</p>
      <p><strong>Registrazione degli accessi:</strong> per finalità di sicurezza e tracciabilità (art. 32 GDPR) vengono registrati eventi tecnici quali login riusciti/falliti, modifiche di password, caricamento/modifica/eliminazione di documenti da parte dell'amministratore, download effettuati dagli utenti. I log sono accessibili esclusivamente all'amministratore.</p>

      <h2>Diritti dell'interessato</h2>
      <p>Puoi in qualsiasi momento richiedere accesso, rettifica, cancellazione, limitazione o portabilità dei tuoi dati, nonché revocare il consenso. Scrivi a bionutrix.supplements@gmail.com.</p>
      <h2>Conservazione</h2>
      <p>Conserviamo i dati per il tempo necessario alle finalità dichiarate, oltre agli obblighi di legge.</p>
      <p className="italic text-xs">Questa policy verrà aggiornata dall'amministratore al completamento della verifica legale.</p>
    </LegalShell>
  );
}

export function CookiePage() {
  const { lang } = useI18n();
  if (lang === "en") {
    return (
      <LegalShell title="Cookie Policy">
        <p>We use technical cookies necessary for the operation of the site. With your consent, we may use anonymised analytics cookies. We do not use profiling or advertising cookies without your explicit consent.</p>
        <h2>Cookie types</h2>
        <ul>
          <li><strong>Necessary</strong> — essential to site operation.</li>
          <li><strong>Analytics</strong> — help us understand site usage in an aggregated and anonymous way (only active with consent).</li>
          <li><strong>Marketing</strong> — used for personalised communications (only active with consent).</li>
        </ul>
        <h2>Managing preferences</h2>
        <p>You can change or revoke your consent at any time by clicking "Cookie preferences" in the site footer.</p>
        <h2>Third-party content</h2>
        <p>Maps, videos and other external content are blocked preventively until consent is given.</p>
      </LegalShell>
    );
  }
  return (
    <LegalShell title="Cookie Policy">
      <p>Utilizziamo cookie tecnici necessari al funzionamento del sito. Con il tuo consenso possiamo utilizzare cookie analitici anonimizzati. Non utilizziamo cookie di profilazione o pubblicitari senza il tuo esplicito consenso.</p>
      <h2>Tipi di cookie</h2>
      <ul>
        <li><strong>Necessari</strong> — indispensabili al funzionamento del sito.</li>
        <li><strong>Analytics</strong> — aiutano a comprendere l'utilizzo del sito in modo aggregato e anonimo (attivi solo previo consenso).</li>
        <li><strong>Marketing</strong> — utilizzati per comunicazioni personalizzate (attivi solo previo consenso).</li>
      </ul>
      <h2>Gestione delle preferenze</h2>
      <p>Puoi modificare o revocare in qualsiasi momento il tuo consenso cliccando su "Preferenze cookie" nel footer del sito.</p>
      <h2>Contenuti di terze parti</h2>
      <p>Mappe, video e altri contenuti esterni vengono bloccati preventivamente fino al consenso.</p>
    </LegalShell>
  );
}

export function TermsPage() {
  const { lang } = useI18n();
  if (lang === "en") {
    return (
      <LegalShell title="Terms & Conditions">
        <p>This website is owned by BioNutrix Srl. Accessing and browsing implies acceptance of these terms.</p>
        <h2>Site content</h2>
        <p>The content on this site is informational. It does not replace veterinary or medical advice. Ingredients, dosage and warnings are reported cautiously and must always be compared with the official label on the packaging: in case of differences, the label prevails.</p>
        <h2>Purchases</h2>
        <p>Currently BioNutrix products are purchased through Amazon. The conditions of sale, shipping and returns are those indicated on Amazon.</p>
        <h2>Intellectual property</h2>
        <p>Trademarks, images, texts and materials on the site are the property of BioNutrix Srl or their respective owners.</p>
        <h2>Limitation of liability</h2>
        <p>BioNutrix Srl is not responsible for improper use of the information published. Before administering any product in particular situations, consult your veterinarian or doctor.</p>
      </LegalShell>
    );
  }
  return (
    <LegalShell title="Termini e condizioni">
      <p>Il sito bionutrix.info è di proprietà di BioNutrix Srl. L'accesso e la navigazione implicano l'accettazione dei presenti termini.</p>
      <h2>Contenuti del sito</h2>
      <p>I contenuti del sito hanno finalità informative. Non sostituiscono il parere del medico veterinario o del medico. Ingredienti, modalità d'uso e avvertenze sono riportati con prudenza e devono essere sempre confrontati con l'etichetta ufficiale della confezione: in caso di differenze prevale l'etichetta.</p>
      <h2>Acquisti</h2>
      <p>Attualmente gli acquisti dei prodotti BioNutrix avvengono tramite Amazon. Le condizioni di vendita, spedizione e reso sono quelle indicate su Amazon.</p>
      <h2>Proprietà intellettuale</h2>
      <p>Marchi, immagini, testi e materiali del sito sono di proprietà di BioNutrix Srl o dei rispettivi titolari.</p>
      <h2>Limitazione di responsabilità</h2>
      <p>BioNutrix Srl non è responsabile per un uso improprio delle informazioni pubblicate. Prima di somministrare qualunque prodotto in situazioni particolari, consulta il veterinario o il medico.</p>
    </LegalShell>
  );
}

export function AccessibilityPage() {
  const { lang } = useI18n();
  if (lang === "en") {
    return (
      <LegalShell title="Accessibility">
        <p>BioNutrix is committed to making this site accessible to the widest possible audience, in line with WCAG 2.2 Level AA guidelines.</p>
        <h2>Our commitment</h2>
        <ul>
          <li>Keyboard navigation and visible focus.</li>
          <li>Adequate contrast.</li>
          <li>Alternative text for images.</li>
          <li>Accessible labels in forms.</li>
          <li>Respect for reduced motion preferences.</li>
        </ul>
        <p>If you encounter difficulties accessing the content, write to bionutrix.supplements@gmail.com — we will act as soon as possible.</p>
      </LegalShell>
    );
  }
  return (
    <LegalShell title="Accessibilità">
      <p>BioNutrix si impegna a rendere questo sito accessibile al maggior numero di persone possibile, in linea con le raccomandazioni WCAG 2.2 livello AA.</p>
      <h2>Impegno</h2>
      <ul>
        <li>Navigazione da tastiera e focus visibile.</li>
        <li>Contrasti adeguati.</li>
        <li>Testi alternativi per le immagini.</li>
        <li>Etichette accessibili nei moduli.</li>
        <li>Rispetto della preferenza di riduzione del movimento.</li>
      </ul>
      <p>Se riscontri difficoltà di accesso ai contenuti, scrivi a bionutrix.supplements@gmail.com per segnalarcelo: interverremo il prima possibile.</p>
    </LegalShell>
  );
}
